import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../config';
import { Card, CardContent, CircularProgress, TextField, InputAdornment, Typography, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import UplaodForm from '../../components/forms/UploadForm/UploadForm';

class HouseholdFiles extends Component {
    constructor(props) {
        super(props);
        this.state = {
            files: [],
            loading: true,
            searchTerm: '',
            householdMembers: [],
            deleteFile: null,
            deleteModalOpen: false,
            show_upload: false
        };
    }

    componentDidMount() {
        this.fetchFiles();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.household_id !== this.props.household_id) {
            this.fetchFiles();
        }
        if (prevState.show_upload !== this.state.show_upload && !this.state.show_upload) {
            this.fetchFiles();
        }
    }

    fetchFiles = async () => {
        this.setState({ loading: true });
        try {
            const { household_id } = this.props;

            // Fetch household members
            const membersResponse = await axios.get(`${APIURL}/households/members`, {
                params: { household_id }
            });
            const members = membersResponse.data;
            const customers = members.filter(m => m.customer_id !== null);

            this.setState({ householdMembers: customers });

            // Fetch files for each customer
            let allFiles = [];
            for (const member of customers) {
                try {
                    const filesResponse = await axios.get(`${APIURL}/artifacts`, {
                        params: { cus_id: member.customer_id }
                    });
                    
                    const customerFiles = filesResponse.data
                        .filter(file => !!file.show)
                        .map(file => ({
                            ...file,
                            customer_name: member.cus_name,
                            customer_id: member.customer_id
                        }));

                    allFiles.push(...customerFiles);
                } catch (error) {
                    console.error(`Error fetching files for customer ${member.customer_id}:`, error);
                }
            }

            // Sort by date
            allFiles.sort((a, b) => new Date(b.submitted) - new Date(a.submitted));

            this.setState({ files: allFiles, loading: false });
        } catch (error) {
            console.error('Error fetching household files:', error);
            this.setState({ loading: false });
        }
    };

    getFilteredFiles = () => {
        const { files, searchTerm } = this.state;
        if (!searchTerm.trim()) return files;

        const searchRegex = new RegExp(searchTerm, 'i');
        return files.filter(file => {
            return (
                searchRegex.test(file.file_name || '') ||
                searchRegex.test(file.customer_name || '') ||
                searchRegex.test(file.tas_title || '') ||
                searchRegex.test(file.pro_title || '') ||
                searchRegex.test(file.empName || '')
            );
        });
    };

    getFileIcon = (fileType) => {
        const type = fileType?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(type)) {
            return <ImageIcon style={{ fontSize: 50, color: '#4CAF50' }} />;
        } else if (type === 'pdf') {
            return <PictureAsPdfIcon style={{ fontSize: 50, color: '#f44336' }} />;
        } else if (['doc', 'docx', 'txt', 'rtf'].includes(type)) {
            return <DescriptionIcon style={{ fontSize: 50, color: '#2196F3' }} />;
        } else {
            return <InsertDriveFileIcon style={{ fontSize: 50, color: '#9e9e9e' }} />;
        }
    };

    handleFileClick = (url) => {
        window.open(url, '_blank');
    };

    handleDownload = (file) => {
        const link = document.createElement('a');
        link.href = file.url;
        link.download = file.file_name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    handleDeleteClick = (file) => {
        this.setState({ deleteFile: file, deleteModalOpen: true });
    };

    deleteFile = async () => {
        try {
            const delResponse = await axios.delete(`${APIURL}/artifacts`, { 
                data: { id: this.state.deleteFile.id } 
            });
            
            if (delResponse.data.code === 200) {
                const updatedFiles = this.state.files.filter((file) => file.id !== this.state.deleteFile.id);
                this.setState({ 
                    files: updatedFiles, 
                    deleteFile: null, 
                    deleteModalOpen: false 
                });
            } else {
                console.log('File not deleted', delResponse.data);
                this.setState({ deleteFile: null, deleteModalOpen: false });
            }
        } catch (error) {
            console.error('Error deleting file:', error);
            this.setState({ deleteFile: null, deleteModalOpen: false });
        }
    };

    renderFileCard = (file) => {
        const fileType = file.url ? file.url.split('.').pop() : '';
        const displayName = file.file_name && !file.file_name.toLowerCase().endsWith(`.${fileType.toLowerCase()}`) 
            ? `${file.file_name}.${fileType}` 
            : file.file_name;
        
        return (
            <Card 
                key={file.id} 
                sx={{ 
                    backgroundColor: '#d4f1f9',
                    transition: 'all 0.2s',
                    '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    }
                }}
            >
                <CardContent style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        {/* Left Column: Icon, File Type, and Buttons */}
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            minWidth: '120px'
                        }}>
                            <div 
                                style={{ 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                                onClick={() => this.handleFileClick(file.url)}
                            >
                                {this.getFileIcon(fileType)}
                                <Chip 
                                    label={fileType.toUpperCase()} 
                                    size="small" 
                                    style={{ marginTop: '8px' }}
                                />
                            </div>
                            
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '8px',
                                marginTop: '16px',
                                width: '100%'
                            }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleDownload(file);
                                    }}
                                    fullWidth
                                >
                                    Download
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleDeleteClick(file);
                                    }}
                                    fullWidth
                                >
                                    Delete
                                </Button>
                            </div>
                        </div>

                        {/* Right Column: File Info */}
                        <div style={{ 
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            minWidth: 0
                        }}>
                            <div>
                                <Typography 
                                    variant="body2" 
                                    style={{ 
                                        fontWeight: 'bold',
                                        marginBottom: '12px',
                                        wordBreak: 'break-word',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => this.handleFileClick(file.url)}
                                >
                                    {displayName}
                                </Typography>
                                
                                <Typography variant="body2" style={{ color: '#1976d2', fontWeight: 500, marginBottom: '4px' }}>
                                    {this.props.customerLabel || 'Customer'}: {file.customer_name}
                                </Typography>
                                
                                {file.tas_title && (
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                                        Task: {file.tas_title}
                                    </Typography>
                                )}
                                
                                {file.pro_title && (
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                                        Project: {file.pro_title}
                                    </Typography>
                                )}
                                
                                {file.empName && (
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                                        Uploaded by: {file.empName}
                                    </Typography>
                                )}
                            </div>
                            
                            <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
                                {moment(file.submitted).format('MMM DD, YYYY')}
                            </Typography>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    render() {
        const { loading, searchTerm } = this.state;
        const filteredFiles = this.getFilteredFiles();

        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </div>
            );
        }

        return (
            <div>
                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                    <TextField
                        value={searchTerm}
                        onChange={(e) => this.setState({ searchTerm: e.target.value })}
                        placeholder="Search files..."
                        variant="outlined"
                        size="small"
                        style={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => this.setState({ show_upload: true })}
                    >
                        Upload
                    </Button>
                </div>

                {filteredFiles.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                        <InsertDriveFileIcon style={{ fontSize: '80px', color: '#ccc', marginBottom: '20px' }} />
                        <Typography variant="h6" color="textSecondary">
                            {searchTerm ? 'No files found matching your search' : 'No files found for this household'}
                        </Typography>
                    </div>
                ) : (
                    <div style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: '20px',
                            paddingBottom: '20px'
                        }}>
                            {filteredFiles.map(file => this.renderFileCard(file))}
                        </div>
                    </div>
                )}

                <Dialog open={this.state.show_upload} onClose={() => this.setState({ show_upload: false })}>
                    <DialogActions>
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            sx={{ p: 0.25 }}
                            onClick={() => this.setState({ show_upload: false })}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogActions>
                    <DialogContent sx={{ paddingTop: '0px', width: 370 }}>
                        <UplaodForm 
                            company_id={this.props.user?.comp_id} 
                            mode='add' 
                            user={this.props.user} 
                            customer_id={null} 
                            onSubmit={() => {
                                this.setState({ show_upload: false });
                                this.fetchFiles();
                            }}
                        />
                    </DialogContent>
                </Dialog>

                <Dialog open={this.state.deleteModalOpen} onClose={() => this.setState({ deleteModalOpen: false })} fullWidth maxWidth="sm">
                    <DialogTitle>Delete File {this.state.deleteFile ? this.state.deleteFile.file_name : ''}</DialogTitle>
                    <DialogContent>
                        <hr style={{ margin: '10px 0', backgroundColor: '#e0e0e0' }} />
                        <Typography>Are you sure you want to delete this file? This action cannot be undone.</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
                        <Button variant="outlined" onClick={() => this.setState({ deleteModalOpen: false, deleteFile: null })}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={this.deleteFile}>Delete</Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

export default HouseholdFiles;
